import React, { useState, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAgentContext } from '../hooks/useAgentContext';
import { CardSkeleton } from '../components/common/LoadingIndicators';
import {
  Button,
  Card,
  Badge,
  Input,
  Select,
  Modal,
  Alert,
  PlusIcon,
  TrashIcon,
  CpuChipIcon,
  ArrowRightIcon,
  FilterIcon,
} from '../components/ui';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { BusinessCaseSummary } from '../services/agent/AgentService';
import {
  isCompletedStatus,
  isInProgressStatus,
} from '../constants/agentProjectStatuses';

// Status display configuration
const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: 'default' | 'primary' | 'success' | 'warning' | 'error';
  }
> = {
  DISCOVERY: { label: 'Discovery', variant: 'primary' },
  DESIGN: { label: 'Design', variant: 'primary' },
  TECHNICAL_ARCHITECTURE: {
    label: 'Technical Architecture',
    variant: 'primary',
  },
  IMPLEMENTATION_PLANNING: {
    label: 'Implementation Planning',
    variant: 'primary',
  },
  CODE_GENERATION: { label: 'Code Generation', variant: 'primary' },
  OPERATIONS_PLANNING: { label: 'Operations Planning', variant: 'primary' },
  COMPLETE: { label: 'Complete', variant: 'success' },
  INTAKE: { label: 'Intake', variant: 'default' },
  PRD_DRAFTING: { label: 'PRD Drafting', variant: 'warning' },
  PRD_REVIEW: { label: 'PRD Review', variant: 'warning' },
  PRD_APPROVED: { label: 'PRD Approved', variant: 'success' },
};

const getStatusConfig = (status: string) => {
  return (
    STATUS_CONFIG[status] || {
      label: status.replace(/_/g, ' '),
      variant: 'default' as const,
    }
  );
};

// Filter options
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'design', label: 'Design' },
];

const SORT_OPTIONS = [
  { value: 'updated_desc', label: 'Recently Updated' },
  { value: 'updated_asc', label: 'Oldest Updated' },
  { value: 'created_desc', label: 'Recently Created' },
  { value: 'created_asc', label: 'Oldest Created' },
  { value: 'title_asc', label: 'Title A-Z' },
  { value: 'title_desc', label: 'Title Z-A' },
];

const ProjectsListPage: React.FC = () => {
  useDocumentTitle('Projects');

  const navigate = useNavigate();
  const {
    cases,
    isLoadingCases,
    fetchUserCases,
    casesError,
    deleteCase,
    isLoading,
  } = useAgentContext();

  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] =
    useState<BusinessCaseSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch cases on mount
  React.useEffect(() => {
    fetchUserCases();
  }, [fetchUserCases]);

  // Filter and sort cases
  const filteredCases = useMemo(() => {
    let result = [...cases];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((c) => c.title.toLowerCase().includes(query));
    }

    // Apply status filter using centralized status helpers
    if (statusFilter !== 'all') {
      result = result.filter((c) => {
        switch (statusFilter) {
          case 'in_progress':
            return isInProgressStatus(c.status);
          case 'completed':
            return isCompletedStatus(c.status);
          case 'discovery':
            return c.status.toLowerCase() === 'discovery';
          case 'design':
            return c.status.toLowerCase() === 'design';
          default:
            return true;
        }
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'updated_desc':
          return (
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
        case 'updated_asc':
          return (
            new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
          );
        case 'created_desc':
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case 'created_asc':
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case 'title_asc':
          return a.title.localeCompare(b.title);
        case 'title_desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    return result;
  }, [cases, searchQuery, statusFilter, sortBy]);

  // Handle delete
  const handleDeleteClick = (
    project: BusinessCaseSummary,
    e: React.MouseEvent
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectToDelete(project);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    setDeleteError(null);
    const success = await deleteCase(projectToDelete.case_id);

    if (success) {
      setDeleteModalOpen(false);
      setProjectToDelete(null);
    } else {
      setDeleteError('Failed to delete project. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setProjectToDelete(null);
    setDeleteError(null);
  };

  const handleContinue = (project: BusinessCaseSummary) => {
    navigate(`/projects/${project.case_id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          <CpuChipIcon className="w-8 h-8 mr-3 text-primary-600" />
          <h1 className="text-3xl font-bold text-secondary-900">
            All Projects
          </h1>
        </div>
        <RouterLink to="/projects/new">
          <Button variant="primary" leftIcon={<PlusIcon className="w-5 h-5" />}>
            Design New Agent
          </Button>
        </RouterLink>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="!p-4">
          <p className="text-sm text-secondary-600">Total</p>
          <p className="text-2xl font-bold text-primary-600">{cases.length}</p>
        </Card>
        <Card className="!p-4">
          <p className="text-sm text-secondary-600">In Progress</p>
          <p className="text-2xl font-bold text-warning-600">
            {cases.filter((c) => isInProgressStatus(c.status)).length}
          </p>
        </Card>
        <Card className="!p-4">
          <p className="text-sm text-secondary-600">Completed</p>
          <p className="text-2xl font-bold text-success-600">
            {cases.filter((c) => isCompletedStatus(c.status)).length}
          </p>
        </Card>
        <Card className="!p-4">
          <p className="text-sm text-secondary-600">This Week</p>
          <p className="text-2xl font-bold text-info-600">
            {
              cases.filter((c) => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return new Date(c.updated_at) >= weekAgo;
              }).length
            }
          </p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<FilterIcon className="w-5 h-5 text-secondary-400" />}
            />
          </div>
          <div className="sm:w-48">
            <Select
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
            />
          </div>
          <div className="sm:w-48">
            <Select
              options={SORT_OPTIONS}
              value={sortBy}
              onChange={(value) => setSortBy(value)}
            />
          </div>
        </div>
      </Card>

      {/* Error State */}
      {casesError && (
        <Alert variant="error" className="mb-6">
          Failed to load projects: {casesError.message}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchUserCases()}
            className="ml-4"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Loading State */}
      {isLoadingCases && (
        <Card>
          <CardSkeleton rows={5} />
        </Card>
      )}

      {/* Empty State */}
      {!isLoadingCases && filteredCases.length === 0 && (
        <Card className="text-center py-12">
          <CpuChipIcon className="w-16 h-16 mx-auto text-secondary-300 mb-4" />
          {cases.length === 0 ? (
            <>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                No projects yet
              </h3>
              <p className="text-secondary-600 mb-6">
                Start designing your first AI agent project
              </p>
              <RouterLink to="/projects/new">
                <Button
                  variant="primary"
                  leftIcon={<PlusIcon className="w-5 h-5" />}
                >
                  Design New Agent
                </Button>
              </RouterLink>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                No matching projects
              </h3>
              <p className="text-secondary-600 mb-4">
                Try adjusting your search or filters
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </>
          )}
        </Card>
      )}

      {/* Projects List */}
      {!isLoadingCases && filteredCases.length > 0 && (
        <Card>
          <ul className="divide-y divide-secondary-200">
            {filteredCases.map((project) => {
              const statusConfig = getStatusConfig(project.status);
              return (
                <li key={project.case_id}>
                  <div className="py-4 px-2 hover:bg-secondary-50 transition-colors rounded flex items-center justify-between gap-4">
                    <RouterLink
                      to={`/projects/${project.case_id}`}
                      className="flex-1 min-w-0"
                    >
                      <div>
                        <p className="font-medium text-secondary-900 truncate">
                          {project.title}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <Badge variant={statusConfig.variant}>
                            {statusConfig.label}
                          </Badge>
                          <span className="text-xs text-secondary-500">
                            Updated:{' '}
                            {new Date(project.updated_at).toLocaleDateString()}
                          </span>
                          <span className="text-xs text-secondary-400">
                            Created:{' '}
                            {new Date(project.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </RouterLink>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleContinue(project)}
                        rightIcon={<ArrowRightIcon className="w-4 h-4" />}
                      >
                        Continue
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteClick(project, e)}
                        className="text-error-600 hover:text-error-700 hover:bg-error-50"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Results count */}
      {!isLoadingCases && cases.length > 0 && (
        <p className="text-sm text-secondary-500 mt-4 text-center">
          Showing {filteredCases.length} of {cases.length} projects
        </p>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        title="Delete Project"
      >
        <div className="space-y-4">
          <p className="text-secondary-600">
            Are you sure you want to delete{' '}
            <strong>{projectToDelete?.title}</strong>?
          </p>
          <Alert variant="warning">
            This action cannot be undone. All project data will be permanently
            removed.
          </Alert>
          {deleteError && <Alert variant="error">{deleteError}</Alert>}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDeleteConfirm}
              disabled={isLoading}
              className="bg-error-600 hover:bg-error-700"
            >
              {isLoading ? 'Deleting...' : 'Delete Project'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectsListPage;
